import MobilePullRefreshIndicator from '@/wap/components/MobilePullRefreshIndicator.vue'
import { classifyMobileTouchGesture } from '@/utils/mobileTouchGesture'

const PULL_DIRECTION_THRESHOLD = 12
const PULL_VERTICAL_RATIO = 1.4
const PULL_COMMIT_DISTANCE = 64

export default {
  components: { MobilePullRefreshIndicator },
  data() {
    return {
      mobilePullRefreshTouchState: null,
      mobilePullRefreshDistance: 0,
      mobilePullRefreshThreshold: 88,
      mobilePullRefreshMinVisibleDuration: 360,
      mobilePullRefreshing: false,
      mobilePullRefreshResetTimer: null,
      mobilePullRefreshRequestSeq: 0
    }
  },
  beforeDestroy() {
    this.resetMobilePullRefreshState()
  },
  methods: {
    getMobilePullRefreshScrollTarget() {
      const target = this.$refs.mobilePullRefreshArea || this.$refs.paperListArea
      return Array.isArray(target) ? target[0] : target
    },
    isMobilePullRefreshBlocked() {
      return !!(
        this.mobilePullRefreshing
        || this.interactionLocked
        || this.paperListAppendLoading
        || this.dailyPreviewOpen
        || this.dailyTabMenuOpen
        || this.dailyPeriodMenuOpen
        || this.dailyFinishMenuOpen
      )
    },
    isMobilePullRefreshInteractiveTarget(target) {
      if (!target || typeof target.closest != 'function') {
        return false
      }
      const interactive = target.closest('input, textarea, select, button, [contenteditable="true"]')
      return !!(interactive && !interactive.closest('.daily-card'))
    },
    canStartMobilePullRefresh(event, target) {
      const area = this.getMobilePullRefreshScrollTarget()
      return !!(
        area
        && target
        && area.contains(target)
        && area.scrollTop <= 0
        && !this.isMobilePullRefreshBlocked()
        && !this.isMobilePullRefreshInteractiveTarget(target)
        && event.touches
        && event.touches.length == 1
      )
    },
    handleMobilePullRefreshStart(event) {
      const target = event && event.target
        ? (event.target.nodeType == 1 ? event.target : event.target.parentElement)
        : null
      if (!this.canStartMobilePullRefresh(event, target)) {
        this.cancelMobilePullRefreshGesture()
        return
      }
      const touch = event.touches[0]
      this.mobilePullRefreshTouchState = {
        startX: touch.clientX,
        startY: touch.clientY,
        startTime: Date.now(),
        direction: '',
        committed: false,
        gestureType: 'pending'
      }
    },
    handleMobilePullRefreshMove(event) {
      const state = this.mobilePullRefreshTouchState
      const area = this.getMobilePullRefreshScrollTarget()
      if (!state || !area) {
        return
      }
      if (event.defaultPrevented) {
        this.cancelMobilePullRefreshGesture()
        return
      }
      if (!event.touches || event.touches.length != 1) {
        this.cancelMobilePullRefreshGesture()
        return
      }
      const touch = event.touches[0]
      const deltaX = touch.clientX - state.startX
      const deltaY = touch.clientY - state.startY
      const absX = Math.abs(deltaX)
      const absY = Math.abs(deltaY)
      if (state.committed) {
        if (event.cancelable) {
          event.preventDefault()
        }
        return
      }
      const gestureType = classifyMobileTouchGesture(state, touch, {
        moveTolerance: PULL_DIRECTION_THRESHOLD,
        verticalRatio: PULL_VERTICAL_RATIO,
        pullDistance: PULL_COMMIT_DISTANCE
      })
      if (gestureType == 'pending') {
        return
      }
      state.gestureType = gestureType
      if (typeof this.clearMobileFileTouchTimer == 'function') {
        this.clearMobileFileTouchTimer()
      }
      if (typeof this.clearDailyMobileTouchTimer == 'function') {
        this.clearDailyMobileTouchTimer()
      }
      if (typeof this.clearGlobalSearchTouchTimer == 'function') {
        this.clearGlobalSearchTouchTimer()
      }
      if (typeof this.clearRecentTouchTimer == 'function') {
        this.clearRecentTouchTimer()
      }
      if (gestureType == 'selection' || gestureType == 'scroll' || area.scrollTop > 0) {
        this.mobilePullRefreshDistance = 0
        if (gestureType == 'selection') {
          this.cancelMobilePullRefreshGesture()
        }
        return
      }
      if (gestureType != 'pull-refresh' || deltaY <= 0 || absY <= absX * PULL_VERTICAL_RATIO) {
        this.mobilePullRefreshDistance = 0
        return
      }
      this.mobilePullRefreshDistance = 0
      if (event.cancelable) {
        event.preventDefault()
      }
      if (deltaY < PULL_COMMIT_DISTANCE || this.isMobilePullRefreshBlocked()) {
        return
      }
      state.committed = true
      this.startMobilePullRefresh()
    },
    handleMobilePullRefreshEnd() {
      this.mobilePullRefreshTouchState = null
      this.mobilePullRefreshDistance = 0
    },
    startMobilePullRefresh() {
      if (this.mobilePullRefreshing) {
        return
      }
      this.clearMobilePullRefreshResetTimer()
      const refreshId = this.mobilePullRefreshRequestSeq + 1
      this.mobilePullRefreshRequestSeq = refreshId
      this.mobilePullRefreshing = true
      this.mobilePullRefreshDistance = 0
      const startedAt = Date.now()
      let refreshTask
      try {
        refreshTask = this.performMobilePullRefresh()
      } catch (error) {
        refreshTask = Promise.reject(error)
      }
      Promise.resolve(refreshTask).catch(() => {}).finally(() => {
        if (this._isBeingDestroyed || this._isDestroyed || refreshId != this.mobilePullRefreshRequestSeq) {
          return
        }
        const elapsed = Date.now() - startedAt
        const delay = Math.max(0, this.mobilePullRefreshMinVisibleDuration - elapsed)
        this.mobilePullRefreshResetTimer = window.setTimeout(() => {
          this.mobilePullRefreshResetTimer = null
          if (refreshId == this.mobilePullRefreshRequestSeq) {
            this.resetMobilePullRefreshState()
          }
        }, delay)
      })
    },
    performMobilePullRefresh() {
      if (this.isDailyMode && typeof this.loadDailyList == 'function') {
        return this.loadDailyList({ silent: true })
      }
      if (typeof this.loadPaperList == 'function') {
        return this.loadPaperList({ pageIndex: 1, silent: true, preserveScroll: true })
      }
      return Promise.resolve()
    },
    cancelMobilePullRefreshGesture() {
      this.mobilePullRefreshTouchState = null
      this.mobilePullRefreshDistance = 0
    },
    clearMobilePullRefreshResetTimer() {
      if (this.mobilePullRefreshResetTimer) {
        clearTimeout(this.mobilePullRefreshResetTimer)
        this.mobilePullRefreshResetTimer = null
      }
    },
    resetMobilePullRefreshState() {
      this.clearMobilePullRefreshResetTimer()
      this.mobilePullRefreshRequestSeq += 1
      this.mobilePullRefreshTouchState = null
      this.mobilePullRefreshDistance = 0
      this.mobilePullRefreshing = false
    }
  }
}
